import React, {
  createContext,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react";

import { Spinner } from "../components/ui/spinner";
import { useAppBranding } from "../customer-branding/apply/hooks";
import { appBrandDocument } from "../customer-branding/apply/overlay";
import {
  injectAppBrandingCss,
  removeAppBrandingCss,
} from "../customer-branding/apply/tokens";
import { isNeedl } from "../utils/isEnv";

import { brandingSchema } from "./types";
import { applyDocumentBranding, getEnvBranding, loadBranding } from "./utils";

import type { AppBrand } from "../customer-branding/apply/types";
import type { BrandingType } from "./types";
import type { ReactNode } from "react";

interface BrandingContextType {
  activeBranding: BrandingType;
  appBrand?: AppBrand;
}

const BrandingContext = createContext<BrandingContextType | undefined>(
  undefined,
);

export const useBranding = () => {
  const context = useContext(BrandingContext);
  if (!context) {
    throw new Error("useBranding must be used within BrandingProvider");
  }
  return context;
};

interface BrandingProviderProps {
  children: ReactNode;
  userBranding?: string;
}

export const BrandingProvider: React.FC<BrandingProviderProps> = ({
  children,
  userBranding,
}) => {
  const envBranding = getEnvBranding();
  const needlDeployment = isNeedl();
  const { brand: appBrand } = useAppBranding();

  const [brandingEpoch, setBrandingEpoch] = useState(0);
  const cssLoaded = brandingEpoch > 0;

  const validatedBranding =
    needlDeployment && userBranding
      ? brandingSchema.safeParse(userBranding)
      : null;

  const activeBranding = validatedBranding?.success
    ? validatedBranding.data
    : envBranding;

  useEffect(() => {
    if (!needlDeployment) return;

    loadBranding(activeBranding)
      .catch((error) => console.error("Failed to load branding:", error))
      .finally(() => setBrandingEpoch((epoch) => epoch + 1));
  }, [activeBranding, needlDeployment]);

  const hadBrand = useRef(false);

  useEffect(() => {
    if (!appBrand) {
      /** Only on the way out. Running this whenever no brand is applied fired a
          load nothing waited for, which could land after one was applied. */
      if (!hadBrand.current) return;
      hadBrand.current = false;
      removeAppBrandingCss();
      void loadBranding(activeBranding);
      return;
    }
    hadBrand.current = true;

    injectAppBrandingCss(appBrand);
    applyDocumentBranding(appBrandDocument(appBrand));
    /** Keyed on the epoch, not a one-way flag: loadBranding re-runs whenever the
        base brand changes and would otherwise land after this and win. */
  }, [appBrand, activeBranding, brandingEpoch]);

  useEffect(() => removeAppBrandingCss, []);

  const value = useMemo(
    () => ({ activeBranding, appBrand }),
    [activeBranding, appBrand],
  );

  if (needlDeployment && !cssLoaded) {
    return <Spinner size="lg" className="mx-auto" />;
  }

  return (
    <BrandingContext.Provider value={value}>
      {children}
    </BrandingContext.Provider>
  );
};
